/// <amd-module name="Repos/Views/PullRequest/Components/ActivityFeed/ActivityCards/FileCommentCard" />
import * as React from "react";
import { Diff } from "Repos/Changes/Components/Diff/Diff";
import { getGitNavigationService, IGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChange, IFileDiff } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext, GitPullRequestCommentThread } from "Repos/Common/Contracts/Git";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getFileName, getIconNameForFile } from "Repos/Common/Util/File";
import { PullRequestDiscussion } from "Repos/Views/PullRequest/Discussion/Components/PullRequestDiscussion";
import { getPullRequestDiscussionService } from "Repos/Views/PullRequest/Discussion/PullRequestDiscussionThreadService";
import { getPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Icon, IconSize, IIconProps } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { css } from "VSSUI/Util";
import { getDiscussionThreadFileDiff } from "../../../Util/Diff";
import { getDiffForThread } from "../Utils/ActivityFeedUtils";
import { IActivityCardSharedProps } from "./ActivityCardSubtle";

interface IInlineCommentCardProps extends IActivityCardSharedProps {
    onAddComment?: () => void;
    onCancelComment?: () => void;
    onDeleteComment?: () => void;
    onUpdateComment?: () => void;
}
interface IState {
    belongsToPreviousIteration: boolean;
}

export class InlineCommentCard extends VssComponent<IInlineCommentCardProps, IState> {
    private extraLinesAfter = new ObservableValue<number>(3);
    private extraLinesBefore = new ObservableValue<number>(3);
    private showOriginalDiff = new ObservableValue<boolean>(false);

    private change: IChange | undefined;
    private fetchedDiff = new ObservableValue(false);
    private fileDiff: IReadonlyObservableValue<IFileDiff | undefined>;
    private isShim: IReadonlyObservableValue<boolean> | undefined;
    private discussionService;
    private prDetailsService;
    private threadContext;

    constructor(props: IInlineCommentCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { belongsToPreviousIteration: false };
        this.prDetailsService = getPullRequestViewService(this.context.pageContext);
        const latestChanges = this.prDetailsService.getLatestChanges();
        const refreshChanges = () => this.forceUpdate();
        latestChanges.subscribe(refreshChanges);
        this.addUnmountCallback(() => latestChanges.unsubscribe(refreshChanges));
        this.isShim = props.isShimObservable;
        this.setBelongsToPreviousIteration = this.setBelongsToPreviousIteration.bind(this);

        this.discussionService = getPullRequestDiscussionService(this.context.pageContext);

        this.threadContext = this.discussionService.getThreadContextsForLatestChanges()[this.props.thread.id] || this.props.thread.threadContext;

        this.prDetailsService
            .getIterationChanges(
                this.prDetailsService.getLatestIterationId(),
                this.props.thread.pullRequestThreadContext.iterationContext?.firstComparingIteration
            )
            .then(result => {
                if (
                    this.prDetailsService.getIterationsSupport() &&
                    this.props.thread.pullRequestThreadContext.iterationContext &&
                    this.props.thread.pullRequestThreadContext.iterationContext.firstComparingIteration !==
                        this.prDetailsService.getLatestIterationId() &&
                    result?.changesByPath[this.threadContext.filePath]
                ) {
                    this.setBelongsToPreviousIteration(true);
                }
            });
    }

    public render() {
        const { onAddComment, onCancelComment, onDeleteComment, onUpdateComment, thread, threadModel } = this.props;
        const { pageContext } = this.context;

        if (!this.threadContext) {
            return null;
        }

        const { filePath, leftFileEnd, rightFileEnd } = this.threadContext;

        const reposRoutingService = getReposRoutingService(pageContext);

        const latestChanges = this.prDetailsService.getLatestChanges().value;
        this.change = latestChanges.changesByPath[filePath];

        let iconProps: IIconProps;
        let changeUrl = "";
        let iterationId: string | undefined = undefined;
        if (!this.change) {
            iconProps = { iconName: getIconNameForFile(filePath) };
            iterationId = thread.pullRequestThreadContext.iterationContext?.firstComparingIteration.toString();
        } else {
            iconProps = this.change.iconProps;
        }

        changeUrl = reposRoutingService.getPullRequestUrl(
            this.prDetailsService.getTargetRepository().name,
            this.prDetailsService.getTargetRepository().project.name,
            this.prDetailsService.getId(),
            "files",
            filePath,
            iterationId,
            undefined,
            thread.id
        );

        let hasLineInfo = false;
        if (leftFileEnd) {
            hasLineInfo = leftFileEnd.line > 0;
        } else if (rightFileEnd) {
            hasLineInfo = rightFileEnd.line > 0;
        }

        const prThreadContext = this.discussionService.getThreadContext();

        return (
            <Card className="flex-row flex-grow no-padding" contentProps={{ contentPadding: false }}>
                <div className="flex-column flex-grow scroll-hidden">
                    <div className="comment-file-header flex-column">
                        <div className="comment-file-header-title flex-row">
                            <div className="body-s secondary-text flex-self-center">
                                <Icon {...iconProps} className={css("repos-change-summary-file-icon", iconProps.className)} />
                            </div>
                            <div className="flex-column flex-start flex-grow scroll-hidden">
                                <FPSLink
                                    className="comment-file-header-link body-m font-weight-semibold text-ellipsis"
                                    href={changeUrl}
                                    removeUnderline={true}
                                >
                                    {getFileName(filePath)}
                                </FPSLink>
                                <span className="body-s secondary-text text-ellipsis flex-self-stretch">{filePath}</span>
                            </div>
                            <div className="flex-row flex-noshrink flex-center">
                                {this.state.belongsToPreviousIteration && (
                                    <Observer showOriginalDiff={this.showOriginalDiff}>
                                        {({ showOriginalDiff }: { showOriginalDiff: boolean }) => (
                                            <Button onClick={this.toggleOriginalDiff} subtle>
                                                {showOriginalDiff ? Resources.ActivityFeedViewLatestDiff : Resources.ActivityFeedViewOriginalDiff}
                                            </Button>
                                        )}
                                    </Observer>
                                )}
                                <Ago className="activity-card-time body-s secondary-text flex-noshrink margin-left-8" date={thread.publishedDate} />
                            </div>
                        </div>
                        <Observer fetchedDiff={this.fetchedDiff}>
                            {() => {
                                return (
                                    <Observer diff={this.fileDiff} extraLinesAfter={this.extraLinesAfter} extraLinesBefore={this.extraLinesBefore}>
                                        {(props: { diff: IFileDiff | undefined; extraLinesAfter: number; extraLinesBefore: number }) => {
                                            const diffThreadContext = this.showOriginalDiff.value
                                                ? getOriginalThreadContext(thread)
                                                : this.threadContext;

                                            const partialDiff =
                                                props.diff && !props.diff.binaryContent
                                                    ? getDiffForThread(props.diff, diffThreadContext, props.extraLinesAfter, props.extraLinesBefore)
                                                    : undefined;
                                            return hasLineInfo && (!props.diff || (props.diff && !props.diff.binaryContent)) ? (
                                                <div className="comment-file-diff-container margin-top-16 flex-row">
                                                    {this.change && (
                                                        <>
                                                            {partialDiff && (
                                                                <div className="code-view-buttons flex-column">
                                                                    <div className="flex-column">
                                                                        {partialDiff.hasLinesBefore && (
                                                                            <Button
                                                                                ariaLabel={Resources.ExpandCodeUpward}
                                                                                className="expand-code-button"
                                                                                iconProps={{ iconName: "ChevronUpMed", size: IconSize.small }}
                                                                                onClick={() => {
                                                                                    this.extraLinesBefore.value += 10;
                                                                                }}
                                                                                subtle={true}
                                                                            />
                                                                        )}
                                                                        {props.extraLinesBefore > 3 && (
                                                                            <Button
                                                                                ariaLabel={Resources.ExpandCodeDownward}
                                                                                className="expand-code-button"
                                                                                iconProps={{
                                                                                    iconName: "ChevronDownMed",
                                                                                    size: IconSize.small
                                                                                }}
                                                                                onClick={() => {
                                                                                    this.extraLinesBefore.value -= 10;
                                                                                }}
                                                                                subtle={true}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-column">
                                                                        {props.extraLinesAfter > 3 && (
                                                                            <Button
                                                                                ariaLabel={Resources.ExpandCodeUpward}
                                                                                className="expand-code-button"
                                                                                iconProps={{ iconName: "ChevronUpMed", size: IconSize.small }}
                                                                                onClick={() => {
                                                                                    this.extraLinesAfter.value -= 10;
                                                                                }}
                                                                                subtle={true}
                                                                            />
                                                                        )}
                                                                        {partialDiff.hasLinesAfter && (
                                                                            <Button
                                                                                ariaLabel={Resources.ExpandCodeDownward}
                                                                                className="expand-code-button"
                                                                                iconProps={{
                                                                                    iconName: "ChevronDownMed",
                                                                                    size: IconSize.small
                                                                                }}
                                                                                onClick={() => {
                                                                                    this.extraLinesAfter.value += 10;
                                                                                }}
                                                                                subtle={true}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <Diff
                                                                getCommentPositions={() => [diffThreadContext]}
                                                                diff={partialDiff && partialDiff.diff}
                                                                onViewFile={e => {
                                                                    const navigationService: IGitNavigationService =
                                                                        getGitNavigationService(pageContext);
                                                                    navigationService.setSelectedItem(this.change!);
                                                                    e.preventDefault();
                                                                }}
                                                                onVisible={this.fetchDiff}
                                                            />
                                                        </>
                                                    )}
                                                    {!this.change && (
                                                        <div className="margin-horizontal-20 padding-left-4 flex-row rhythm-horizontal-8">
                                                            <Status {...Statuses.Information} key="success" size={StatusSize.m} />
                                                            {latestChanges.loadMoreChanges ? (
                                                                <FormatComponent format={Resources.ActivityFeedOnlyFirstChangesLoadedFormat}>
                                                                    <span>{latestChanges.totalChanges}</span>
                                                                    <Link onClick={() => latestChanges.loadMoreChanges!()}>
                                                                        {Resources.ActivityFeedLoadMore}
                                                                    </Link>
                                                                </FormatComponent>
                                                            ) : (
                                                                <span className="margin-left-16">
                                                                    <span>{Resources.ActivityFeedFileDoesNotExist}</span>
                                                                    <FPSLink href={changeUrl}>
                                                                        {Resources.ActivityFeedViewFileOriginalContext}
                                                                    </FPSLink>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : null;
                                        }}
                                    </Observer>
                                );
                            }}
                        </Observer>
                    </div>
                    <Observer isShim={this.isShim}>
                        {(props: { isShim: boolean | undefined }) => (
                            <PullRequestDiscussion
                                author={prThreadContext.currentUser}
                                onAddComment={onAddComment}
                                onCancelComment={onCancelComment}
                                onCancelThread={this.onDeleteThread}
                                onDeleteComment={onDeleteComment}
                                onDeleteThread={this.onDeleteThread}
                                onChangeStatus={this.onChangeStatus}
                                onUpdateComment={onUpdateComment}
                                permissions={prThreadContext.permissions}
                                personaProvider={prThreadContext.personaProvider}
                                pullRequestId={prThreadContext.pullRequestId}
                                repository={prThreadContext.repository}
                                thread={threadModel}
                                isShim={props.isShim}
                            />
                        )}
                    </Observer>
                </div>
            </Card>
        );
    }

    private fetchDiff = async () => {
        if (this.change) {
            this.fileDiff = await getDiscussionThreadFileDiff(this.context.pageContext, this.props.thread, this.change, this.showOriginalDiff.value);
            this.fetchedDiff.value = true;
        }
    };
    private setBelongsToPreviousIteration(value: boolean) {
        this.setState({ belongsToPreviousIteration: value });
    }
    private toggleOriginalDiff = () => {
        this.showOriginalDiff.value = !this.showOriginalDiff.value;
        this.fetchDiff();
    };

    private onDeleteThread = (threadId: number): void => {
        getPullRequestDiscussionService(this.context.pageContext).removeThread(threadId);
    };

    private onChangeStatus = (): void => {
        getPullRequestDiscussionService(this.context.pageContext).updateThreadStatus();
    };
}

function getOriginalThreadContext(thread: GitPullRequestCommentThread) {
    if (!thread.pullRequestThreadContext || !thread.pullRequestThreadContext.trackingCriteria) {
        return thread.threadContext;
    }

    const { origFilePath, origLeftFileEnd, origLeftFileStart, origRightFileEnd, origRightFileStart } =
        thread.pullRequestThreadContext.trackingCriteria;

    const originalThreadContext = {
        filePath: origFilePath
    } as CommentThreadContext;

    if (origLeftFileEnd) {
        originalThreadContext.leftFileEnd = origLeftFileEnd;
    }
    if (origLeftFileStart) {
        originalThreadContext.leftFileStart = origLeftFileStart;
    }
    if (origRightFileEnd) {
        originalThreadContext.rightFileEnd = origRightFileEnd;
    }
    if (origRightFileStart) {
        originalThreadContext.rightFileStart = origRightFileStart;
    }

    return originalThreadContext;
}

Components.add("ms.vss-code-web.file-comment-activity-card", InlineCommentCard);
